import { r as createLogger } from "../logger-CdXeOlrX.js";
import { drawMapData } from "./map-overlay-draw.js";
import { addOsmTileLayer } from "./osm-tile-layer.js";
import L from "leaflet";
//#region ../src/visualization/summary-map-shell.ts
/**
* The 2D summary map's shell: a Leaflet map with the OSM basemap, the
* shared trajectory layers, the app's own layers on top, an optional
* fullscreen toggle, and a clean `destroy`.
*
* MOVED OUT OF THE RECORDERAPP (DEC-H3, Tour Viewer authoring plan
* 2026-09-28-0953 §3.3). The Recorder's session summary drew its map here
* since 2026-01; the Tour Viewer's summary after Finish is the second
* consumer. What differs between them is passed in, never forked:
*
* - `drawExtra` - the app's own layers (the Recorder's reference points,
*   the Tour Viewer's codes, facing lines and pins), drawn after the
*   trajectory and removed with the map;
* - `fitPoints` - frame these points instead of centring on the final
*   position. The Recorder centres (far-away prior reference points must
*   not shrink the walk to a dot, 2026-06-02); the Tour Viewer frames its
*   codes and objects, which can exist without any walk;
* - `fullscreen` - the toggle's classes. They are the app's styling (the
*   Recorder's Tailwind utilities, the Tour Viewer's own CSS), so the
*   framework names none of them; without it no toggle is drawn.
*
* Leaflet is imported statically HERE, so an app that must not ship it to
* every visitor imports this module dynamically (the Tour Viewer does).
*
* @see summary-map-shell.ts.md
*/
const log = createLogger("SummaryMapShell");
/** The zoom a centred map opens at: roughly a city block. */
const SUMMARY_MAP_INITIAL_ZOOM = 15;
/** Pixel padding of a framed view, so markers and rings at the edge are
*  not clipped. */
const SUMMARY_MAP_FIT_PADDING = [20, 20];
/**
* The closest a framed view zooms in. One code and one pin a metre apart
* would otherwise open at the tiles' ceiling, where the basemap says
* nothing about where the place is. 18 shows about 150 m across a phone.
*/
const SUMMARY_MAP_FIT_MAX_ZOOM = 18;
/** Delay (ms) before the first `invalidateSize`, for a container that was
*  not laid out yet when the map was created. */
const FIRST_RESIZE_DELAY_MS = 100;
/** Delay (ms) before re-measuring after a fullscreen transition. */
const TOGGLE_RESIZE_DELAY_MS = 300;
/** The view's centre when nothing is framed: the final raw position, else
*  the final fused one. */
function finalPosition(data) {
	return data.rawGpsPath.at(-1) ?? data.fusedPath.at(-1);
}
/** Frame the points, or centre on `centre` at `zoom`. */
function frameView(map, fitPoints, centre, zoom) {
	if (fitPoints.length > 0) {
		const bounds = L.latLngBounds(fitPoints.map((p) => [p.lat, p.lng]));
		map.fitBounds(bounds, {
			padding: SUMMARY_MAP_FIT_PADDING,
			maxZoom: SUMMARY_MAP_FIT_MAX_ZOOM
		});
	} else if (centre !== void 0) map.setView([centre.lat, centre.lng], zoom);
}
/** Draw every layer: the basemap, the trajectory, the app's. */
function drawLayers(map, data, drawExtra) {
	const layers = [addOsmTileLayer(map)];
	const drawn = drawMapData(map, {
		userPosition: null,
		rawGpsPath: [...data.rawGpsPath],
		fusedPath: [...data.fusedPath],
		alignmentSnapshots: [...data.alignmentSnapshots ?? []]
	});
	layers.push(...drawn.layers, ...drawExtra?.(map) ?? []);
	return layers;
}
/** The fullscreen toggle: its two buttons in `container`, the classes they
*  swap, and `refit` after each transition. */
function createToggle(container, toggle, refit) {
	const { expand, collapse } = toggleButtons(toggle);
	let expanded = false;
	let disposed = false;
	function setExpanded(next) {
		if (disposed || expanded === next) return;
		expanded = next;
		container.classList.remove(...next ? toggle.inlineClasses : toggle.expandedClasses);
		container.classList.add(...next ? toggle.expandedClasses : toggle.inlineClasses);
		expand.classList.toggle(toggle.hiddenClass, next);
		collapse.classList.toggle(toggle.hiddenClass, !next);
		refit();
	}
	const onExpand = () => {
		setExpanded(true);
	};
	const onCollapse = () => {
		setExpanded(false);
	};
	const position = getComputedStyle(container).position;
	if (!container.style.position && (position === "" || position === "static")) container.style.position = "relative";
	expand.addEventListener("click", onExpand);
	collapse.addEventListener("click", onCollapse);
	container.append(expand, collapse);
	return {
		setExpanded,
		isExpanded: () => expanded,
		dispose: () => {
			disposed = true;
			expand.removeEventListener("click", onExpand);
			collapse.removeEventListener("click", onCollapse);
			expand.remove();
			collapse.remove();
		}
	};
}
/** Remove every layer, then the map; a failing removal is logged, never
*  thrown (a half-removed map must not break the page's cleanup). */
function removeAll(map, layers) {
	for (const layer of layers) try {
		layer.remove();
	} catch (err) {
		log.warn("Ignoring error during layer cleanup:", err);
	}
	try {
		map.remove();
	} catch (err) {
		log.warn("Error during map cleanup:", err);
	}
}
/**
* Create the map in `container`.
*
* @returns null when there is no container, nothing to show (no path and
*   no `fitPoints`), or Leaflet or an app layer throws.
*/
function createSummaryMapShell(container, data, options = {}) {
	const fitPoints = options.fitPoints ?? [];
	const centre = finalPosition(data);
	if (container === null) {
		log.warn("Cannot create summary map: container is null");
		return null;
	}
	if (centre === void 0 && fitPoints.length === 0) {
		log.warn("Cannot create summary map: nothing to show");
		return null;
	}
	return buildShell(container, data, options, {
		fitPoints,
		centre
	});
}
/** {@link createSummaryMapShell} past its preconditions. */
function buildShell(container, data, options, view) {
	const { fitPoints, centre } = view;
	let map = null;
	try {
		const created = L.map(container);
		map = created;
		const layers = drawLayers(created, data, options.drawExtra);
		frameView(created, fitPoints, centre, options.initialZoom ?? 15);
		let destroyed = false;
		const firstResize = setTimeout(() => {
			created.invalidateSize();
		}, FIRST_RESIZE_DELAY_MS);
		let toggleResize = null;
		const refit = () => {
			if (toggleResize !== null) clearTimeout(toggleResize);
			toggleResize = setTimeout(() => {
				created.invalidateSize();
				frameView(created, fitPoints, centre, created.getZoom());
			}, TOGGLE_RESIZE_DELAY_MS);
		};
		const toggle = options.fullscreen === void 0 ? null : createToggle(container, options.fullscreen, refit);
		return {
			destroy: () => {
				if (destroyed) return;
				destroyed = true;
				clearTimeout(firstResize);
				if (toggleResize !== null) clearTimeout(toggleResize);
				toggle?.dispose();
				removeAll(created, layers);
			},
			expand: () => {
				toggle?.setExpanded(true);
			},
			collapse: () => {
				toggle?.setExpanded(false);
			},
			isExpanded: () => toggle?.isExpanded() ?? false
		};
	} catch (err) {
		log.error("Failed to create summary map:", err);
		try {
			map?.remove();
		} catch {}
		return null;
	}
}
/** The two toggle buttons, the collapse one hidden. */
function toggleButtons(toggle) {
	const make = (testId, text, title) => {
		const button = document.createElement("button");
		button.type = "button";
		button.setAttribute("data-testid", testId);
		button.className = toggle.buttonClassName;
		button.textContent = text;
		button.title = title;
		button.setAttribute("aria-label", title);
		return button;
	};
	const expand = make("btn-map-expand", "⛶", "Enlarge map");
	const collapse = make("btn-map-collapse", "✕", "Close fullscreen");
	collapse.classList.add(toggle.hiddenClass);
	return {
		expand,
		collapse
	};
}
//#endregion
export { SUMMARY_MAP_FIT_PADDING, SUMMARY_MAP_INITIAL_ZOOM, createSummaryMapShell };
